/**
 * VibrantLabOrderModal.tsx
 *
 * Thin Vibrant adapter over the shared LabOrderModalCore. Vibrant's SFTP
 * account has no facility, insurance/billing or diagnoses, so those sections
 * are disabled; the form keeps location, provider, patient demographics, tests
 * (searched against the labs_vibrant_ordcode catalog: the location's own codes
 * plus Vibrant's compendium codes, which every location can order) and
 * presets, which sit in the Location bar because they are per location.
 *
 * The core builds a QuestOrderPayload-shaped object; this adapter maps the
 * subset Vibrant needs into VibrantOrderPayload and submits it to the native
 * /vibrant-orders endpoint (HL7 build + SFTP upload happen server-side).
 */
import {
  getVibrantProviders,
  getVibrantLocations,
  searchVibrantTests,
  submitVibrantOrder,
  getVibrantPresets,
  getVibrantPresetContent,
  saveVibrantPreset,
  type VibrantOrderPayload,
  type QuestOrderPayload,
} from '@/services/labs.service';
import axios from 'axios';
import { getErrorCode, getErrorMessage } from '@/services/apiClient';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult, type ParsedLabPreset } from './LabOrderModalCore';
import { parseVibrantPreset } from './vibrantPreset';

interface VibrantLabOrderModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
  onOrdered: () => void;
}

const vibrantConfig: LabOrderConfig = {
  vendor: 'vibrant',
  title: 'Vibrant Lab Order',
  // Presets carry a location, so they sit next to the Location picker. Nothing
  // waits for a location: with none picked, presets list them all and loading
  // one picks its location.
  presetsInLocationBar: true,
  testsBeforeDetails: true,
  // The server sends the chart's name, birth date and gender.
  lockFilledIdentity: true,
  features: {
    presets: true,
    facilityProvider: false,
    providerPicker: true,
    locationPicker: true,
    testSearchOnEmpty: true,
    billing: false,
    diagnoses: false,
    vitals: false,
    notes: false,
    // OBR-7 carries a time; without one Vibrant records the draw at midnight.
    collectionTime: true,
  },
  // No location yet: only the codes orderable at every location.
  searchTests: (query, routingId) => searchVibrantTests(query, routingId, { globalOnly: !routingId }),
  getProviders: getVibrantProviders,
  getLocations: getVibrantLocations,
  getPresets: getVibrantPresets,
  getPresetContent: getVibrantPresetContent,
  parsePreset: (content, kind, defaultDate): ParsedLabPreset => parseVibrantPreset(content, kind, defaultDate),
  savePreset: (payload: QuestOrderPayload & { presetTitle: string; presetShared: string }) =>
    saveVibrantPreset({
      presetTitle: payload.presetTitle,
      presetShared: payload.presetShared,
      routingId: payload.routingId ?? '',
      tests: payload.tests.map((t) => ({
        testCode: t.testCode,
        description: t.description ?? '',
        obrNotes: t.obrNotes,
      })),
    }),
  submit: async (payload): Promise<LabOrderSubmitResult> => {
    const collectionDate = payload.tests[0]?.collectionDate ?? '';
    const order: VibrantOrderPayload = {
      patientId: payload.patientId,
      firstName: payload.firstName,
      middleName: payload.middleName,
      lastName: payload.lastName,
      birthMonth: payload.birthMonth,
      birthDay: payload.birthDay,
      birthYear: payload.birthYear,
      street: payload.street,
      city: payload.city,
      state: payload.state,
      zip: payload.zip,
      phone: payload.phone,
      gender: payload.gender,
      collectionDate,
      providerId: payload.provider,
      routingId: payload.routingId,
      tests: payload.tests.map((t) => ({
        testCode: t.testCode,
        collectionDate: t.collectionDate,
        obrNotes: t.obrNotes,
      })),
      pidNotes: payload.pidNotes,
      internalNotes: payload.internalNotes,
      externalNotes: payload.externalNotes,
    };

    // The API's own words: the order was sent and is waiting for Vibrant to
    // confirm it. Sent is not delivered; the order summary is that proof.
    let result;
    try {
      result = await submitVibrantOrder(order);
    } catch (err) {
      // The send ran out of time but may still reach Vibrant. Show the server's
      // words as the result: back on the form, Submit would place a second order.
      if (getErrorCode(err) === 'VIBRANT_ORDER_PENDING') {
        const details = axios.isAxiosError(err)
          ? (err.response?.data as { error?: { details?: { orderId?: number } } } | undefined)?.error?.details
          : undefined;
        const message = getErrorMessage(err, "This order may still reach Vibrant. Don't send it again yet.");
        return {
          ok: true,
          lines: details?.orderId ? [`Order #${details.orderId}`, message] : [message],
        };
      }
      throw err;
    }
    return {
      ok: true,
      lines: [`Order #${result.orderId}`, result.message],
    };
  },
};

export function VibrantLabOrderModal({ patientId, open, onClose, onOrdered }: VibrantLabOrderModalProps) {
  return (
    <LabOrderModalCore
      config={vibrantConfig}
      patientId={patientId}
      open={open}
      onClose={onClose}
      onOrdered={onOrdered}
    />
  );
}
